import { addDays, addMonths, endOfDay, formatISO, startOfDay, startOfMonth } from "date-fns";
import { TerminationOfExistingContract } from "features/contracting/enums/terminationOfExistingContract";
import PageCard from "features/shared/components/card/PageCard";
import DatePicker from "features/shared/components/reactDates/DatePicker";
import { TerminationSchemeEnumDTO } from "features/shared/enums/dynamic/TerminationSchemeEnumDTO";
import FormGroup from "features/shared/forms/components/FormGroup";
import FormGroupTooltip from "features/shared/forms/components/formGroup/FormGroupTooltip";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import { useAfterInit } from "features/shared/hooks/useAfterInit";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";
import React, { useEffect } from "react";

type Props = {
  formHandler: UseFormHookResult;
  terminationSchemes: TerminationSchemeEnumDTO[];
};

const ContractExpectedEffectiveDate = ({
  formHandler,
  terminationSchemes,
}: Props) => {
  //translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "contractExpectedEffectiveDate");
  
  // form hook props
  const {
    bindDatePickerField,
    getFormValue,
    setFormValue,
    clearFormValues,
    clearValidationErrors,
    errors,
  } = {
    ...formHandler,
  };

  // after init hook
  const afterInit = useAfterInit();

  // form value getters
  const getProcessType = () =>
    getFormValue<TerminationOfExistingContract>(
      "terminationOfExistingContract"
    );

  const getTerminationSchemeCode = () =>
    getFormValue<string>("yesTerminationByUs.terminationSchemeCode");

  const getContractEffectiveDate = (objectPrefix: string): string | undefined =>
    getFormValue(`${objectPrefix}.contractEffectiveDate`);

  // side-effects
  // calculate expceted date by termination scheme
  useEffect(() => {
    const schemeCode = getTerminationSchemeCode();
    if (schemeCode) {
      setDateByScheme(schemeCode);
      clearValidationErrors(["contractExpectedEffectiveDate"]);
    }
  }, [getTerminationSchemeCode()]);
  // calculate expceted date by effective date (yesTerminationByUs asnwer)
  useEffect(() => {
    setDateByEffectiveDate("yesTerminationByUs")
  }, [getContractEffectiveDate("yesTerminationByUs")]);
  // calculate expceted date by effective date (noFixedContratDuration asnwer)
  useEffect(() => {
    setDateByEffectiveDate("noFixedContratDuration")
  }, [getContractEffectiveDate("noFixedContratDuration")]);

  // in process change clear expected date
  useEffect(() => {
    if (afterInit) {
      clearFormValues(["contractExpectedEffectiveDate"]);  
    }
  }, [getProcessType()]);

  // helpers
  const isDefaultDateAnswer = () => {
    const defaultDateAnswers: TerminationOfExistingContract[] = [
      "YesTerminationByUs",
      "NoFixedContratDuration",
    ];
    const answer = getProcessType();
    return defaultDateAnswers.includes(answer);
  };

  const setDateByScheme = (schemeCode: string) => {
    const scheme = terminationSchemes.find((x) => x.code === schemeCode);

    if (scheme && scheme.contractDurationType === "IndefiniteTerm") {
      let date = startOfDay(new Date());

      switch (scheme.unit) {
        case "Day":
          date = addDays(date, scheme.duration!);
          break;
        case "Month":
          date = addMonths(date, scheme.duration!);
          break;
        case "Quarter":
          date = addMonths(date, scheme.duration! * 3);
          break;
        default:
          break;
      }

      // first day of next month
      date = startOfMonth(addMonths(date, 1));

      const isoDate = formatISO(date, { representation: "date" });
      setFormValue("contractExpectedEffectiveDate", isoDate);
    }
  };

  const setDateByEffectiveDate = (objectPrefix: string) => {
    const effectiveDate = getContractEffectiveDate(objectPrefix);
    if (effectiveDate) {
      const date = addDays(new Date(effectiveDate), 1); // plus one day to effective date
      const isoDate = formatISO(date, {
        representation: "date",
      });

      setFormValue("contractExpectedEffectiveDate", isoDate);
      clearValidationErrors(["contractExpectedEffectiveDate"]);
    }
  };

  const getMinDateByProcessType = (): Date => { 
    const answer = getProcessType();
    const actualDate = endOfDay(new Date());

    // TODO: datumy budou upresneny
    switch (answer) {
      case "NoTerminationMyself":
        return addDays(actualDate, 1);
      case "NoContractByAnother":
        return addDays(actualDate, 5);
      case "NoPlaceOfDeliveryNotConnected":
        return addDays(actualDate, 10);
      case "NoPlaceOfDeliveryNotExists":
        return addDays(actualDate, 10);
      default:
        break;
    }

    return new Date();
  }

  const getInfoTooltip = (): FormGroupTooltip | undefined => {
    if (isDefaultDateAnswer()) {
      return {
        id: "contractExpectedEffectiveDate-tooltip",
        content: tLabels("tooltip"),
        placement: "top",
      };
    }

    return undefined;
  };

  // render
  return (
    <PageCard
      title={tLabels("cardTitle")}
      bodyClassName="pt-4 px-4"
      cardClassName="mb-4"
    >
      <div className="col-md-6 col-lg-12 pl-0 pr-0 pr-md-2 pr-lg-0">
        <FormGroup
          labelText={tLabels("contractExpectedEffectiveDate")}
          inputId="contractExpectedEffectiveDate"
          validationError={errors["contractExpectedEffectiveDate"]}
          tooltip={getInfoTooltip()}
        >
          <DatePicker
            {...bindDatePickerField("contractExpectedEffectiveDate")}
            onlySelectable
            minDate={getMinDateByProcessType()}
            disabled={isDefaultDateAnswer()}
          />
        </FormGroup>
      </div>
    </PageCard>
  );
};

export default ContractExpectedEffectiveDate;
